import { Box, Card, CardContent, LinearProgress, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
	DataGridPremium,
	getGridBooleanOperators,
	getGridDateOperators,
	getGridNumericOperators,
	getGridSingleSelectOperators,
	getGridStringOperators,
	useGridApiRef
} from '@mui/x-data-grid-premium';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'throttle-debounce';
import dayjs from 'dayjs';
import consts from '../../consts';
import { usePrerequisites } from '../../contexts/Prerequisites';
import { useMutationsCount, useSetState, useUniqueEffect } from '../../hooks';
import utils from '../../util';
import AsyncAutocomplete from '../common/AsyncAutocomplete';
import ConfirmDialog from '../confirm';
import { deleteRecord, exportList, getList, printFetch, updateOrdering } from '../crud-helper';
import useCustomLocaleText from '../../hooks/useCustomLocaleText';
import AdminLogsTable from '../../AppGrid/AdminLogDetailTable';
import GridDataContext, { useGridBase } from './GridBaseContext';
import { convertDefaultSort } from './GridPreferencesControl';
import GridBasePagination from './pagination';
import GridBaseToolbar from './toolbar';
import DateTimeFilterInput from './DateTimeFilterInput';

const { isValidFile } = utils;

const { fileTypes } = consts;

export const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	border: 'none',
	'*::-webkit-scrollbar': {
		width: theme.spacing(1),
		height: theme.spacing(1)
	},
	'*::-webkit-scrollbar-track': {
		backgroundColor: theme.palette.background.paper
	},
	'*::-webkit-scrollbar-thumb': {
		borderRadius: theme.spacing(),
		backgroundColor: theme.palette.text.disabled,
		'&:hover': {
			backgroundColor: theme.palette.text.secondary,
		},
		'&:focus': {
			backgroundColor: theme.palette.text.primary,
		}
	},
	'& .MuiAutocomplete-root': {
		width: '100%'
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		color: '#418FDE',
		whiteSpace: 'pre-wrap'
	},
	'& .table-cell-trucate': {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	'& .MuiDataGrid-columnSeparator': {
		visibility: 'hidden'
	},
	'& .MuiDataGrid-columnHeader': {
		height: (theme) => theme.spacing(6),
		'&:focus-within': {
			outlineWidth: `0px !important`
		}
	},
	'& .MuiDataGrid-columnHeaders': {
		'&:hover': {
			'& .MuiDataGrid-columnSeparator': {
				visibility: 'visible',
				width: 'auto'
			}
		}
	},
	'& .MuiDataGrid-columnHeader--filter': {
		paddingTop: '3px',
		paddingBottom: 0
	},
	p: 0,
	'& .MuiDataGrid-filler': {
		'&:last': {
			display: 'none'
		}
	},
	'& .MuiIconButton-root': {
		margin: theme.spacing(0.5)
	},
	'& .MuiDataGrid-footerContainer': {
		minHeight: 'auto'
	}
}));

export const AsyncAutocompleteFilterInput = ({
	column,
	item,
	applyValue,
	onBlur,
	placeholder,
	label,
	onFocus,
	focusElementRef,
	combos,
	headerFilterMenu,
	...rest
}) => {
	const { patch, filterModel } = useGridBase();
	let options = typeof column?.lookup === 'string' ? (typeof combos === 'object' ? combos[column?.lookup] : []) : column?.lookup;
	if (!Array.isArray(options) && typeof options !== 'function') {
		options = [];
	}
	if (typeof column?.lookup === 'string') {
		options = options.map(({ LookupId, DisplayValue, ...option }) => ({
			id: LookupId,
			label: DisplayValue || LookupId,
			...option
		}));
	}

	const handleFilterChange = (event, newValue) => {
		const parsedNewValue =
			typeof column.parseFilterValue === 'function'
				? column.parseFilterValue(newValue)
				: Array.isArray(newValue)
					? newValue.map((entry) => entry.id)
					: newValue?.id;
		let currentFilterItem = {
			...item,
			columnField: column.headerFilterField || column.field || item.columnField,
			value: parsedNewValue
		};
		let existingFilters = [...filterModel.items];
		const removeFilterItem = !currentFilterItem.value;
		if(removeFilterItem) {
			existingFilters = existingFilters.filter((filterItem) => filterItem.columnField !== currentFilterItem.columnField);
			patch({ filterModel: { items: existingFilters }});
			return;
		}
		const filterIndex = existingFilters.findIndex((filterItem) => filterItem.columnField === currentFilterItem.columnField);
		if(filterIndex === -1) {
			existingFilters.push(currentFilterItem);
		} else {
			existingFilters[filterIndex] = currentFilterItem;
		}
		patch({ filterModel : { items: existingFilters } });
	};

	return (
		<AsyncAutocomplete
			name={`${item?.field || 'custom'}-filter-operator"`}
			label={label}
			size="small"
			margin="dense"
			placeholder={placeholder}
			value={item?.value}
			options={options}
			onChange={handleFilterChange}
			autoHighlight
		/>
	);
};

const RelationGrid = forwardRef(({ modelConfig, openRelationIndex, data, onClearSelection, onGridStartFetchData }, ref) => {
	const {
		relation: Relation,
		getParentFilters,
		getUrlSearchParams,
		title: relationTitle
	} = useMemo(
		() => ({ ...(Array.isArray(modelConfig.relations) ? modelConfig.relations[openRelationIndex] : {}) }),
		[openRelationIndex, modelConfig, data]
	);

	const parentFilters = useMemo(() => (typeof getParentFilters === 'function' ? getParentFilters(data) : {}), [data, getParentFilters]);

	useLayoutEffect(() => {
		if (ref.current) {
			ref.current.scrollIntoView();
		}
	}, [ref.current]);

	return (
		<Stack>
			{!!Relation && !!data.selectedId && (
				<Relation
					parentFilters={parentFilters}
					urlSearchParams={typeof getUrlSearchParams === 'function' ? getUrlSearchParams(data) : new URLSearchParams()}
					relationTitle={typeof relationTitle === 'function' ? relationTitle(data) : relationTitle}
					isRelation
					onClearSelection={onClearSelection}
					onGridStartFetchData={onGridStartFetchData}
					tableRef={ref}
				/>
			)}
		</Stack>
	);
});

const components = {
	loadingOverlay: LinearProgress,
	toolbar: GridBaseToolbar,
	pagination: GridBasePagination
};


const pageSizeOptions = [5, 15, 25, 50];
const GridBase = forwardRef(
	(
		{
			useLinkColumn = false,
			modelConfig,
			apiParams,
			idProperty,
			columns,
			api,
			readOnly,
			defaultSort,
			setActiveRecord,
			onSelection,
			refresh,
			parentFilters,
			parent,
			onOpenForm,
			onClearSelection,
			isRelation,
			relationTitle,
			urlSearchParams,
			onGridDataLoad,
			onGridStartFetchData,
			onCellClick,
			refreshInterval,
			disablePreferences,
			hiddenColumns = [],
			...others
		},
		ref
	) => {
		const localeText = useCustomLocaleText();
		const theme = useTheme();
		const auth = useSelector((storeState) => storeState.auth);
		const licenseData = utils.localData?.LicenseAppName;
		const disableExport = false;
		const rowReordering = Boolean(modelConfig?.orderable);
		const combosRef = useRef({});
		const relationGridRef = useRef();
		const columnOrderRef = useRef([]);

		const gridTopRef = useRef();
		const hiddenColumnsRef = useRef([]);
		const [searchParams, setSearchParams] = useSearchParams();
		const navigate = useNavigate();
		const location = useLocation();
		const gridRef = useGridApiRef();
		const hideGridFooter =
			typeof modelConfig.hideGridFooter === 'function' ? modelConfig.hideGridFooter({ location }) : modelConfig.hideGridFooter;
		const hideGridFooterPagination =
			typeof modelConfig.hideGridFooterPagination === 'function'
				? modelConfig.hideGridFooterPagination({ location })
				: modelConfig.hideGridFooterPagination;
		const openRelationIndexParam = useMemo(() => {
			let relation = searchParams.has('relation') ? searchParams.get('relation') : -1;
			if (Array.isArray(modelConfig.relations)) {
				return modelConfig.relations.findIndex(
					(rel, i) => rel.column?.toLowerCase?.() === relation?.toLowerCase?.() || i === Number(relation)
				);
			}
			return -1;
		}, [searchParams, modelConfig]);
		const selectedIdParam = searchParams.has('selected') ? searchParams.get('selected') : null;
		const selectedIdRef = useRef(selectedIdParam || null);
		const gridId =
			!disablePreferences && typeof modelConfig.name === 'string'
				? `${modelConfig.name}${modelConfig.name.endsWith('Table') ? '' : 'Table'}`
				: null;
		const refreshIntervalRef = useRef();

		const initialColumnVisibilityModel = useMemo(() => {
			let allColumns = columns || modelConfig?.getGridColumns(others);
			const visibilityModel = allColumns.reduce((acc, curr) => {
				const hide = typeof curr.hide === 'function' ? curr.hide({ location }) : curr.hide;
				return { ...acc, [curr.field]: !hide };
			}, {});
			
			// Add virtual time columns as hidden
			allColumns.forEach(column => {
				if (['datetime', 'date', 'dateTime'].includes(column.type)) {
					visibilityModel[`${column.field}_Time`] = false; // Hidden by default
				}
			});
			
			return visibilityModel;
		}, [location]);

		const initialColumnWidths = useMemo(()=>{
			let allColumns = columns || modelConfig?.getGridColumns(others);
			return allColumns.map(({field, width}) => ({field, width})) || [];
		}, [])
		const [{ strings }] = usePrerequisites();
		const { logColumns = [] } = modelConfig.importProps || {};
		const [state, setState] = useSetState({
			selectedId: selectedIdParam || null,
			record: null,
			data: { rowCount: 0, rows: [] },
			isLoading: false,
			allColumnsHidden: false,
			confirmDeleteOpen: false,
			openRelationIndex: openRelationIndexParam >= 0 ? openRelationIndexParam : -1,
			standaloneFilters: {},
			filterModel: { items: [] },
			sortModel:
				typeof defaultSort === 'string' || typeof modelConfig?.defaultSort === 'string'
					? convertDefaultSort(defaultSort || modelConfig?.defaultSort)
					: modelConfig?.defaultSort || [],
			page: 0,
			pageSize: modelConfig.pageSize || consts.defaultPageSize,
			paginationModel: { pageSize: modelConfig.pageSize || consts.defaultPageSize, page: 0 },
			ready: !gridId,
			columnVisibilityModel: initialColumnVisibilityModel,
			headerFiltersVisible: false,
			open: false,
			uninserterdEntries: [],
			columnWidths: [],
			rowSelectionModel: { type: 'include', ids: new Set() }, // data type of rowSelectionModel is an object with ids as Set
			initialColumnWidths,
			showHuluMsg: false,
			groupingModel: []
		});
		const hideModal = () => {
			setState({ ...state, open: false });
		};

		const setData = useCallback(
			({ combos, ...data }) => {
				combosRef.current = combos || {};
				setState({ data, fetched: true, ready: true, isLoading: false });
				if (typeof onGridDataLoad === 'function') {
					onGridDataLoad(data, { state, setState, combos: combosRef.current });
				}
				if (typeof modelConfig.onGridDataLoad === 'function') {
					modelConfig.onGridDataLoad(data, { state, setState, combos: combosRef.current });
				}
			},
			[onGridDataLoad, state]
		);
		const setIsLoading = (isLoading) => setState({ isLoading });

		const onDelete = async () => {
			if (selectedIdRef.current) {
				setState({ isLoading: true, confirmDeleteOpen: false });
				await deleteRecord({ id: selectedIdRef.current, api: api || modelConfig?.endPoint, isDelete: true });
				//Refresh
				fetchData();
				selectedIdRef.current = null;
			}
		};

		const deleteRow = (params) => {
			selectedIdRef.current = params.id;
			setState({ selectedId: params.id, confirmDeleteOpen: true, record: null });
		};

		const handleCloseConfirmDialog = () => {
			selectedIdRef.current = null;
			setState({ confirmDeleteOpen: false, selectedId: null });
		};

		const scrollGridIntoView = () => {
			setTimeout(() => {
				gridTopRef.current?.scrollIntoView();
			}, 100);
		};
		const fetchDataCount = useMutationsCount([
			api,
			modelConfig,
			parentFilters,
			state.filterModel,
			state.sortModel,
			state.standaloneFilters,
			state.paginationModel,
			state.ready
		]);
		
		const fetchData = useCallback(
			(force = false, backgroundApi = false) => {
				const isValidFilterModel =
					Array.isArray(state.filterModel?.items) && state.filterModel?.items?.length > 0
						? state.filterModel?.items?.some((item) => 'value' in item && typeof item?.value !== 'undefined')
						: true;
				if (isValidFilterModel && (state.ready || force || disablePreferences)) {
					if (typeof onGridStartFetchData === 'function') {
						onGridStartFetchData();
					}
					getList({
						page: state.paginationModel.page,
						pageSize: state.paginationModel.pageSize,
						sortModel: state.sortModel,
						filterModel: state.filterModel,
						api: api || modelConfig?.endPoint,
						apiParams,
						setIsLoading,
						setData,
						gridColumns,
						standaloneFilterColumns,
						modelConfig,
						parentFilters,
						standaloneFilters: state.standaloneFilters,
						backgroundApi
					});
				}
			},
			[fetchDataCount]
		);
		
		const handleRefresh = useCallback(
			(scrollIntoViewport = true) => {
				setTimeout(() => {
					if (scrollIntoViewport) {
						scrollGridIntoView();
					}
					fetchData(true);
				}, 150);
			},
			[fetchData]
		);

		const columnDepsCount = useMutationsCount([
			columns,
			modelConfig,
			parent,
			licenseData,
			rowReordering,
			hiddenColumns,
			state.ready,
			location.pathname,
			state.columnWidths
		]);

		const getCombos = () => combosRef.current;
		
		const [gridColumns, standaloneFilterColumns] = useMemo(() => {
			let allColumns = columns || modelConfig?.getGridColumns(others);

			const finalColumns = [],
				finalHeaderFilters = [];
			let allHiddenFields = [...(Array.isArray(hiddenColumns) ? hiddenColumns : [])];
			if (columnOrderRef.current?.length > 0) {
				allColumns = allColumns.sort((a, b) => columnOrderRef.current.indexOf(a.field) - columnOrderRef.current.indexOf(b.field));
			}

			const virtualColumns = [];
			for (const column of allColumns) {
				// Add virtual _Time column for datetime fields first
				if (consts.dateTimeTypes.includes(column.type)) {
					const virtualTimeColumn = {
						field: `${column.field}_Time`,
						header: column.header || column.field,
						headerName: column.headerName || column.field,
						type: column.type,
						width: column.width || 150,
						hide: true,
						hidden: true,
						keyColumn: true,
						hideable: false,
						filterable: false,
						sortable: false,
						valueFormatter: column.valueFormatter
					};
					virtualColumns.push(virtualTimeColumn);
				}
			}
			
			// Combine original columns with virtual columns
			allColumns = [...allColumns, ...virtualColumns];

			for (const column of allColumns) {

				const filterDisplayValue = column.customfilterValue
					? [
						{ id: "true", label: column.customfilterValue.trueDisplay },
						{ id: "false", label: column.customfilterValue.falseDisplay }
					]
					: [
						{ id: "true", label: strings.Yes },
						{ id: "false", label: strings.No }
					];
				const excludeOnGrid = typeof column.excludeOnGrid === 'function' ? column.excludeOnGrid({ location, auth }) : column.excludeOnGrid;

				if ((parent && column.lookup === parent) || excludeOnGrid || column.standaloneHeaderFilter) {
					if (column.standaloneHeaderFilter && !excludeOnGrid) {
						finalHeaderFilters.push(column);
					}
					continue;
				}
				if (column.requiredLicense && !licenseData.includes(column.requiredLicense)) {
					continue;
				}
				if (column.disabledLicense && licenseData.includes(column.disabledLicense)) {
					continue;
				}
				if (typeof column.hide === 'function') {
					column.hide = column.hide({ location, auth });
				}
				if (allHiddenFields.includes(column.field) && !column.hide) {
					column.hide = true;
				}
				if (typeof column.customizeHeader === 'function') {
					const customHeader = column.customizeHeader({ location, auth });
					column.header = typeof customHeader === 'string' ? customHeader : column.header;
					column.headerName = strings[column.header] || column.headerName;
				}
				const width = state.columnWidths?.find(({ field }) => field === column.field)?.width || column.width;
				if (typeof width === 'number') {
					column.width = width;
				}
				//Capture combos in thecase of renderCell
				if (Object.keys(column).some((key) => ['renderCell', 'valueGetter', 'valueGetter'].includes(key))) {
					column.getCombos = getCombos;
				}
				if (allHiddenFields.includes(column.field) && !hiddenColumnsRef.current.includes(column.field)) {
					hiddenColumnsRef.current.push(column.field);
				}

				if (column.type === 'actions' || column.type === 'action') {
					column.filterable = false;
					column.sortable = false;
				}
				column.deleteRow = deleteRow;
				column.refreshData = handleRefresh;
				let filterType = (column.filterType || column.type || 'string').toLowerCase();

				if (filterType === 'datetime' && !column.filterType) {
					filterType = 'date';
				}
				if (!('filterOperators' in column)) {
					//Type filter operatore & Temporary patch till backend accepts spectrum filter operators
					if (['int', 'number'].includes(filterType)) {
						column.filterOperators = getGridNumericOperators().filter((operator) => ['=', '<', '>'].includes(operator.value));
					} else if (filterType === 'boolean' || filterType === 'bool') {
						column.filterOperators = getGridBooleanOperators().map((operator) => ({
							...operator,
							InputComponent: (params) => (
								<AsyncAutocompleteFilterInput
									column={{
										...column,
										lookup: filterDisplayValue
									}}
									{...params}
									autoHighlight
								/>
							),
						}));
					} else if (filterType === 'date') {
						const format = column.formFormat || utils.getDateTimeFormat(false).date;
						column.filterOperators = getGridDateOperators()
							.filter(
								(op) => !['after', 'before', 'onOrBefore', 'onOrAfter', 'isEmpty', 'isNotEmpty', 'not'].includes(op.value)
							)
							.map((operator) => ({
								...operator,
								InputComponent: DateTimeFilterInput,
								InputComponentProps: {
									...operator.InputComponentProps,
									format
								}
							}));
					} else if (filterType === 'datetime') {
						const format = column.formFormat || column.format || utils.getDateTimeFormat(column.showSecond).datetime;
						column.filterOperators = getGridDateOperators(true)
							.filter(
								(op) => !['after', 'before', 'onOrBefore', 'onOrAfter', 'isEmpty', 'isNotEmpty', 'not'].includes(op.value)
							)
							.map((operator) => ({
								...operator,
								InputComponent: DateTimeFilterInput,
								InputComponentProps: {
									...operator.InputComponentProps,
									format
								}
							}));
					} else {
						const CustomOperator = [
							{
								value: 'not',
								label: 'Not',
								getApplyFilterFn: (filterItem, column) => {
									if (!filterItem.field || !filterItem.value || !filterItem.operator) {
										return null;
									}

									return (params) => {
										return String(params.value) >= String(filterItem.value);
									};
								},
								InputComponentProps: { type: 'string' }
							},
						];
						const stringOperators = getGridStringOperators(); // Replace with the actual function call
						const allStringOperator = [...stringOperators, ...CustomOperator];
						const filterOptions = !column.header?.includes('Id')
							? ['equals', 'contains', 'not', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty', 'isAnyOf']
							: ['equals', 'isEmpty', 'isNotEmpty', 'not'];

						column.filterOperators = allStringOperator.filter((operator) => filterOptions.includes(operator.value));
					}
				}
				if (!!column.lookup && !column.ignoreLookupOnGridFilter) {
					column.filterOperators = getGridSingleSelectOperators()
						.filter((operator) =>
							operator && operator?.value
								? operator?.value === 'is' || operator?.value === 'not' || operator?.value === 'isAnyOf'
								: false
						)
						.map((operator) => ({
							...operator,
							InputComponent: operator.InputComponent
								? (params) => <AsyncAutocompleteFilterInput {...params} column={column} combos={combosRef.current} />
								: undefined,
						}));
				}

				if (rowReordering) {
					column.sortable = false;
				}
				if (column.headerFilter && !excludeOnGrid) {
					finalHeaderFilters.push(column);
				}
				const standaloneHeaderFilter =
					typeof column.standaloneHeaderFilter === 'function'
						? column.standaloneHeaderFilter({ location, auth })
						: column.standaloneHeaderFilter;
				if (!standaloneHeaderFilter) {
					finalColumns.push(column);
				}
			}
			return [finalColumns, finalHeaderFilters];
		}, [columnDepsCount]);

		const handleOnExport = useCallback(
			(exportFormat = 'CSV') => {
				const visibleColumns = gridRef.current?.getVisibleColumns?.();
				let exportConfig = modelConfig?.extraExportConfig ?? {};
				exportConfig = exportConfig[exportFormat] || {};
				const allGridColumns = visibleColumns ?? gridColumns;
				const exportGridColumns = allGridColumns.filter((column) => !column.isNotExportField && column.name !== 'PrintTape');
				return exportList({
					sortModel: state.sortModel,
					filterModel: state.filterModel,
					standaloneFilters: state.standaloneFilters,
					standaloneFilterColumns,
					api: api || modelConfig?.endPoint,
					title: modelConfig?.title || modelConfig?.name,
					limit: modelConfig.limit || 0,
					setIsLoading: (exporting) => { },
					data: state?.data.rows,
					apiParams,
					gridColumns: exportGridColumns,
					modelConfig,
					hiddenColumns: hiddenColumnsRef.current,
					parentFilters,
					exportFormat,
					exportConfig
				});
			},
			[
				state.hiddenColumns,
				state.sortModel,
				state.filterModel,
				modelConfig?.endPoint,
				api,
				apiParams,
				modelConfig,
				parentFilters,
				state.data
			]
		);

		const handleOnOpenForm = useCallback(
			(id, record = null) => {
				if (typeof onOpenForm === 'function') {
					onOpenForm({
						id,
						api: api || modelConfig?.endPoint,
						setIsLoading,
						setActiveRecord,
						apiParams,
						modelConfig,
						parentFilters,
						record,
						location
					});
				} else if (!readOnly) {
					if ('pathname' in modelConfig && location.pathname !== modelConfig.pathname) {
						const nextUrlSearchParams = urlSearchParams instanceof URLSearchParams ? urlSearchParams : new URLSearchParams();
						if (!nextUrlSearchParams.has('id')) {
							nextUrlSearchParams.set('id', id || 'new');
						}
						navigate(`${modelConfig.pathname}?${nextUrlSearchParams.toString()}`);
					} else {
						const idSearchParam = id ? id : 'new';
						searchParams.set('id', idSearchParam);
						setSearchParams(searchParams);
					}
				}
			},
			[readOnly, onOpenForm, location.pathname, urlSearchParams]
		);
		
		const handleOnCellClick = useCallback(
			(event) => {
				const { row: record } = event;
				const recordId = record.id || event.id;
				if (typeof onCellClick === 'function') {
					onCellClick(event);
				}

				const openRelationIndex = Array.isArray(modelConfig.relations)
					? modelConfig.relations.findIndex((relation) => relation.column === event.field)
					: -1;
				if (event?.colDef?.type === 'action') {
					setState({ selectedId: recordId, record: record, openRelationIndex });
					return;
				}
				if (useLinkColumn && event.field === modelConfig.linkColumn) {
					handleOnOpenForm(recordId, record);
				}
				if (onSelection) {
					onSelection(recordId, {
						...event,
						combos: combosRef.current,
						refresh: handleRefresh,
						scrollIntoView: scrollGridIntoView
					});
				}

				if (openRelationIndex >= 0 && (searchParams.has('relation') || searchParams.has('selected'))) {
					const searchParamsObj = {};
					for (const [key, value] of searchParams.entries()) {
						searchParamsObj[key] = value;
					}

					searchParamsObj.relation = modelConfig.relations[openRelationIndex]?.column ?? openRelationIndex;
					searchParamsObj.selected = recordId;
					if (searchParamsObj.relation === -1) {
						delete searchParamsObj.relation;
					}
					setSearchParams(searchParams);
				}

				if (state.openRelationIndex !== openRelationIndex && openRelationIndex > -1 && relationGridRef.current) {
					relationGridRef.current.scrollIntoView();
				}
				selectedIdRef.current = recordId;
				setState({ selectedId: recordId, record: record, openRelationIndex });
			},
			[state.openRelationIndex, onCellClick, searchParams]
		);

		const onCellDoubleClick = useCallback((event) => {
			const { row: record } = event;
			const recordId = record?.id || event.id;
			handleOnOpenForm(recordId, record);
		}, []);

		const handleOnClickAdd = () => {
			handleOnOpenForm(null);
		};

		const getGridRowId = (row) => {
			return row[idProperty || modelConfig.idProperty];
		};

		const handleImportfile = async (e) => {
			const file = e.target.files[0];
			let response;
			let isPhysician = false;
			let isUserView = true;
			if (modelConfig.gridTitle === consts.userType) {
				isPhysician = true;
			}
			if (consts.usersType.includes(modelConfig.gridTitle)) {
				isUserView = false;
			}
			e.target.value = '';
			let showWarning = true;
			const { message, maxCount = 0, importUrl, type } = modelConfig.importProps || {}; // Destructuring props from importProps
			try {
				if (maxCount) {
					setState({ isLoading: true });
					const response = await fetch(api || modelConfig?.endPoint, {
						method: 'POST',
						body: JSON.stringify({ action: 'list' }),
						headers: { 'Content-Type': 'application/json' }
					})
					const { recordCount } = await response.json();
					if (recordCount >= maxCount) {
						utils.showError(strings[message], strings.WarningTitle, 'warning');
						showWarning = false; // Setting the flag as false to not show further
					}
				}

				const formData = new FormData();
				formData.append('file', file);
				formData.append('action', 'onImport');
				formData.append('importType', type);
				formData.append('isPhysician', isPhysician);
				formData.append('gridTitle', modelConfig.gridTitle);
				response = await fetch(importUrl || api || modelConfig?.endPoint, {
					method: 'POST',
					body: formData
				});
				setState({ isLoading: false });
				const result = await response.json();
				if(result.recordCount >= maxCount && showWarning) {
					utils.showError(strings[message], strings.WarningTitle, 'warning');
				}
				const uninserterdEntries = result?.uninserterdEntries || [];
				if (uninserterdEntries.length) {
					result.uninserterdEntries.forEach((duplicate, index) => {
						duplicate.id = index;
					});
					setState({ open: true, uninserterdEntries: result.uninserterdEntries, showHuluMsg: isUserView });
				}
				
				if (result.info || result.err) {
					const title = result.success ? strings.Info : strings.Error;
					const type = result.success ? 'info' : 'error';
					utils.showError(strings[result.info || result.err] || result.info, title, type);
				}
				handleRefresh();
			} catch (err) {
				console.error(err);
			}
		};

		const printBarcode = async () => {
			try {
				// Make a fetch request to your server to generate the PDF
				let ids = [];
				Array.from(state.rowSelectionModel.ids).map(item => ids.push(item));
				if (ids.length !== 0) {
					const formValue = new FormData();
					formValue.append('action', 'otherAction');
					formValue.append('otherAction', 'printBarcode');
					formValue.append('rowIds', JSON.stringify(ids));
					const response = await printFetch(api || modelConfig?.endPoint, formValue);

					if (!response.ok) {
						throw new Error('Failed to fetch PDF');
					}

					// Convert the response to a blob
					const pdfBlob = await response.blob();

					// Create a URL for the blob
					const pdfUrl = URL.createObjectURL(pdfBlob);
					// Open the PDF in a new window
					window.open(pdfUrl, '_blank');
				} else {
					utils.showError(strings.printValidation, strings.Info, 'info');
				}
			} catch (error) {
				console.error('Error printing barcode:', error);
			}
		};

		const handlePopoverOpen = (event) => {
			const text = event.currentTarget.innerText;
			const title = event.target.getAttribute('title');
			if (!title && Boolean(text)) {
				event.target.setAttribute('title', text);
			}
		};

		const handleRowOrderChange = useCallback(
			async (params) => {
				const idProperty = modelConfig.idProperty;
				if (idProperty) {
					const rowsClone = [...state.data.rows];
					const row = rowsClone.splice(params.oldIndex, 1)[0];
					rowsClone.splice(params.targetIndex, 0, row);
					const keysArr = rowsClone.map((row) => row[idProperty]);
					updateOrdering({
						api: api || modelConfig?.endPoint,
						data: { key: keysArr.join(',') },
						setIsLoading: (isLoading) => setState({ isLoading })
					})
						.then((res) => res.json())
						.then(({ success }) => {
							if (success) {
								fetchData(); //refresh
							}
						});
				}
			},
			[state.data.rows]
		);

		useUniqueEffect(() => {
			if ((state.ready && fetchDataCount > 0) || (!fetchDataCount && state.ready)) {
				fetchData();
			}
		}, [fetchDataCount]);

		const handleOnColumnVisibilityModelChange = useCallback(
			(visibilityModel) => {
				hiddenColumnsRef.current = Object.keys(visibilityModel).filter((column) => {
					return !visibilityModel[column];
				});
				const allColumnsHidden = Object.keys(visibilityModel).filter((name) => {
					const index = gridColumns.findIndex(({ field }) => field === name);
					return index > -1 && !visibilityModel[name];
				});
				setState({
					allColumnsHidden: allColumnsHidden?.length >= gridColumns?.length,
					columnVisibilityModel: visibilityModel
				});
			},
			[gridColumns]
		);

		const handleOnSubmitHeaderFilters = useCallback(
			({ standaloneFilters }) => {
				setState({ standaloneFilters });
			},
			[standaloneFilterColumns, gridColumns]
		);

		const toggleHeaderFiltersVisibility = () => {
			setState((prevState) => ({ headerFiltersVisible: !prevState.headerFiltersVisible, filterModel: { items: [] } }));
		};

		const clearSelection = () => {
			if (searchParams.has('relation') || searchParams.has('selected')) {
				searchParams.delete('relation');
				searchParams.delete('selected');
				setSearchParams(searchParams);
			}
			selectedIdRef.current = null;
			setState({ selectedId: null, record: null, openRelationIndex: -1 });

			if (typeof onClearSelection === 'function') {
				onClearSelection();
			}
		};

		useUniqueEffect(() => {
			if (openRelationIndexParam || selectedIdParam) {
				const selectedId = !state.selectedId ? selectedIdParam : state.selectedId;
				const openRelationIndex = !state.openRelationIndex ? openRelationIndexParam : state.openRelationIndex;
				const record = state.data?.rows?.find?.((row) => row[idProperty || modelConfig.idProperty] === selectedId);
				setState({ openRelationIndex, selectedId, record });
			}
		}, [openRelationIndexParam, selectedIdParam, state?.data?.rows]);

		useEffect(() => {
			const refreshLongPollMs =
				typeof refreshInterval === 'number'
					? refreshInterval
					: modelConfig.gridRefreshInterval > 0
						? modelConfig.gridRefreshInterval
						: null;
			clearInterval(refreshIntervalRef.current);
			refreshIntervalRef.current = null;
			if (refreshLongPollMs > 0 && state.ready) {
				refreshIntervalRef.current = setInterval(() => fetchData(false, true), refreshLongPollMs);
				return () => {
					clearInterval(refreshIntervalRef.current);
					refreshIntervalRef.current = null;
				};
			}
		}, [
			refreshInterval,
			parentFilters,
			state.filterModel,
			state.sortModel,
			state.standaloneFilters,
			state.paginationModel,
			state.ready
		]);

		useUniqueEffect(() => {
			if (state.openRelationIndex >= 0 && relationGridRef.current) {
				relationGridRef.current.scrollIntoView();
			}
		}, [state.openRelationIndex, relationGridRef.current]);

		const isRowSelected = useCallback((selectedId) => selectedId === state.selectedId, [state.selectedId]);

		const handleOnGridStartFetchData = () => {
			if (relationGridRef.current) {
				relationGridRef.current.scrollIntoView();
			}
		};

		const handleOnChangePreference = useCallback(
			({ pagination, sorting, filter, standaloneFilters = {}, columns, columnWidths }, params) => {
				if (!disablePreferences) {
					columnOrderRef.current = columns.columnOrder;
					const skipOperatorsArr = ['isNotEmpty', 'isEmpty'];
					let filterModelItems = Array.isArray(filter?.filterModel?.items) ? filter?.filterModel?.items : [];

					for (let i = 0; i < filterModelItems.length; i++) {
						const item = filterModelItems[i];
						if (skipOperatorsArr.includes(item?.operator)) {
							continue;
						} else if (
							!skipOperatorsArr.includes(item?.operator) &&
							(!('value' in item) || typeof item?.value === 'undefined')
						) {
							filterModelItems.splice(i);
						}
					}
					
					setState((prevState) => ({
						page: pagination?.page || 0,
						pageSize: pagination?.pageSize || consts.defaultPageSize,
						paginationModel: {
							page: pagination?.page || 0,
							pageSize: pagination?.pageSize || consts.defaultPageSize,
						},
						sortModel: sorting?.sortModel || {},
						filterModel: { items: filterModelItems },
						standaloneFilters: standaloneFilters || {},
						columnVisibilityModel: columns?.columnVisibilityModel,
						columnOrder: columns.columnOrder,
						ready: true,
						columnWidths: Array.isArray(columnWidths) ? columnWidths : prevState.columnWidths,
						headerFiltersVisible:
							(!params?.isDefault && filter?.filterModel?.items?.length > 0) || prevState.headerFiltersVisible
					}));
				}
			},
			[state]
		);

		const handleColumnOrderChange = useCallback((params, event, details) => {
			columnOrderRef.current = gridRef.current?.getAllColumns?.()?.map(({ field }) => field);
		}, []);

		const getTogglableColumns = useCallback(
			(columns = []) =>
				Array.isArray(columns)
					? columns
						.filter(({ excludeOnPanelColumns }) => {
							let skip = !!excludeOnPanelColumns;
							if (typeof excludeOnPanelColumns === 'function') {
								skip = excludeOnPanelColumns({ auth, location });
							}
							return !skip;
						})
						.map((column) => column.field)
					: [],
			[auth, location]
		);

		const slotProps = useMemo(
			() => ({
				pagination: {
					onRefresh: handleRefresh,
					showGridRefreshBtn: modelConfig.showGridRefreshBtn,
					disablePagination: modelConfig.disablePagination,
					rowCount: state?.allColumnsHidden ? 0 : state.data?.rowCount,
					loading: state.loading,
					pageSizeOptions
				},
				filterPanel: {
					// No Link operators,
					linkOperators: [],
					filterFormProps: {
						linkOperatorInputProps: {
							variant: 'outlined',
							size: 'small'
						},
						columnInputProps: {
							variant: 'outlined',
							size: 'small',
							sx: { mt: 'auto' }
						},
						operatorInputProps: {
							variant: 'outlined',
							size: 'small',
							sx: { mt: 'auto' }
						},
						valueInputProps: {
							InputComponentProps: {
								variant: 'outlined',
								size: 'small'
							},
						},
					},
					sx: {
						zIndex: (theme) => theme.zIndex.fab,
						'& .MuiDataGrid-filterForm': { p: 2 },
						'& .MuiDataGrid-cell': {
							cursor: 'pointer',
						},
						'& .MuiDataGrid-filterFormLinkOperatorInput': { mr: 2 },
						'& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
						'& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
						'& .MuiDataGrid-filterFormValueInput': { width: 200 },
						'& .MuiDataGrid-menu': {
							zIndex: (theme) => theme.zIndex.fab
						}
					},
				},
				toolbar: {
					onExport: handleOnExport,
					handleOnClickAdd,
					onDelete,
					modelConfig,
					disableExport,
					standaloneFilterColumns,
					onSubmitHeaderFilters: handleOnSubmitHeaderFilters,
					standaloneFilters: state.standaloneFilters,
					combos: combosRef.current,
					readOnly,
					handleImportfile,
					isRelation,
					clearSelection,
					relationTitle: relationTitle,
					defaultSort,
					onChangePreference: handleOnChangePreference,
					gridId,
					defaultColumnVisibilityModel: initialColumnVisibilityModel,
					onRefresh: handleRefresh,
					printBarcode,
					defaultColumnWidths: initialColumnWidths,
				},
				cell: {
					onMouseEnter: handlePopoverOpen
				},
				baseTextField: {
					variant: 'outlined',
					size: 'small',
					margin: 'dense',
					fullWidth: true,
					sx: { minHeight: '49px' }
				},
				columnsManagement: {
					getTogglableColumns
				},
			}),
			[
				combosRef.current,
				modelConfig,
				state.data?.rowCount,
				state.loading,
				state.allColumnsHidden,
				getTogglableColumns,
				handleOnChangePreference
			]
		);

		const handleOnFilterModelChange = debounce(350, (filterModel) => {
			//Debounce to avoid unneccesary fetch
			filterModel.items = filterModel.items
				.filter((item) => item.operator)
				.map((item) => {
					if ([null, undefined].includes(item.value)) {
						item.value = '';
					}
					if (typeof item.value === 'string') {
						item.value = item.value.trim();
					}
					return item;
				});
			setState({ filterModel });
		});
		const handleOnSortModelChange = (sortModel) => setState({ sortModel });
		const getState = useCallback(() => state, [state]);
		const handleOnChangePaginationModel = (paginationModel) => setState({ paginationModel });
		useImperativeHandle(
			ref,
			() => ({
				refresh: handleRefresh,
				scrollIntoView: scrollGridIntoView
			}),
			[handleRefresh]
		);
		const rowCount = useMemo(() => {
			const count = state.allColumnsHidden ? 0 : state.data?.rowCount || 0;
			return count;
		}, [state.allColumnsHiddenopRef, state.data]);

		const minHeight = useMemo(() => {
			const rowHeight = 36;
			const gridToolBarHeight = modelConfig.hideToolbar ? 0 : 48;
			const appximateFilterColumnsWidth = standaloneFilterColumns.length ? Math.ceil(12 / standaloneFilterColumns.length) : 0;
			const standaloneFilterColumnsWidth = standaloneFilterColumns.reduce((columnWidth, filter) => {
				columnWidth += filter.filterColSize || appximateFilterColumnsWidth;
				return columnWidth;
			}, 0);
			const standaloneFilterRows = standaloneFilterColumns.length ? Math.ceil(standaloneFilterColumnsWidth / 12) : 0;
			const standaloneFilterColumnsHeight = (standaloneFilterRows * 52) + (standaloneFilterRows - 1) * 8; // adding the filter height and the spaceing between them
			const headerHeight = 48;
			const headerFilterHeight = state.headerFiltersVisible ? 48 : 0;
			const rowSpan = (state.isLoading || !state.ready) ? rowHeight * 3 : rowCount === 0 ? rowHeight * 2 : 0; // if no rows providing extra space for no rows overlay
			const paginationHeight = modelConfig.disablePagination ? 0 : 60;
			const minGridHeight = gridToolBarHeight + standaloneFilterColumnsHeight + headerHeight + headerFilterHeight + rowSpan + paginationHeight;
			return minGridHeight;
		}, [state.standaloneFilterColumns, modelConfig.disablePagination, rowCount, state.headerFiltersVisible, state.isLoading, state.ready, modelConfig.hideToolbar]);
		
		const onRowSelectionModel = useCallback((rowSelectionModel) => {
			setState({ rowSelectionModel })
		}, [])

		/**
 		* Handles changes to the row grouping model for a data grid.
 		*
 		* Filters the current `gridColumns` to include only those present in the new grouping model.
 		* For columns of type 'datetime', 'date', or 'dateTime', it appends '_Time' to the field name.
 		* Then, updates the grouping model state with the processed fields.
 		*
 		* @param {string[]} newGroupingModel - An array of column field names selected for grouping.
 		*/
		const handleRowGroupingModelChange = (newGroupingModel) => {
			const columnsArr = gridColumns.filter(col => newGroupingModel.includes(col.field));
			const updatedGroupingModel = [];
			columnsArr.forEach((col) => {
				if (['datetime', 'date', 'dateTime'].includes(col.type)) {
					updatedGroupingModel.push(`${col.field}_Time`);
				} else {
					updatedGroupingModel.push(col.field);
				}
			})
			setState((prevState) => ({
				...prevState,
				groupingModel: updatedGroupingModel
			}));
		};

		return (
			<Stack sx={{ marginTop: 0 }}>
				<GridDataContext.Provider
					value={{
						combos: combosRef.current,
						getState,
						getCombos,
						standaloneFilterColumns,
						patch: setState,
						isLoading: state.isLoading,
						refresh: handleRefresh,
						onChangeHeaderFiltersVisibility: toggleHeaderFiltersVisibility,
						rowSelectionModel: state.rowSelectionModel,
						onRowSelectionModel,
						gridColumns,
						filterModel: state.filterModel,
						standaloneFilters: state.standaloneFilters
					}}
				>
					<Box ref={gridTopRef} />

					<Card sx={{ marginBottom: theme.spacing(2) }}>
						<CardContent style={{ display: 'flex', flexDirection: 'column', minHeight, maxHeight: 702 }}>
							<StyledDataGrid
								columnBufferPx={50}
								apiRef={gridRef}
								onCellClick={handleOnCellClick}
								onCellDoubleClick={useLinkColumn === false && onCellDoubleClick}
								onColumnOrderChange={handleColumnOrderChange}
								columns={gridColumns}
								paginationModel={state.paginationModel}
								onPaginationModelChange={handleOnChangePaginationModel}
								isRowSelected={isRowSelected}
								headerStyle={{
									borderBottom: 'none', // Remove the border from the header cells
								}}
								pageSizeOptions={pageSizeOptions}
								pagination
								rowCount={rowCount}
								rows={state.allColumnsHidden ? [] : state.data?.rows || []}
								rowReordering={rowReordering}
								onRowOrderChange={handleRowOrderChange}
								paginationMode="server"
								sortModel={rowReordering ? undefined : state.sortModel}
								filterModel={state.filterModel}
								onFilterModelChange={handleOnFilterModelChange}
								onSortModelChange={handleOnSortModelChange}
								sortingMode={rowReordering ? 'client' : 'server'}
								filterMode="server"
								getRowId={getGridRowId}
								loading={!!state.isLoading || !state.ready}
								initialState={{
									density: 'compact',
								}}
								slots={components}
								showToolbar={!modelConfig.hideToolbar}
								columnVisibilityModel={state.columnVisibilityModel}
								onColumnVisibilityModelChange={handleOnColumnVisibilityModelChange}
								slotProps={slotProps}
								headerFilters={!!state.headerFiltersVisible}
								hideFooterPagination={hideGridFooterPagination}
								hideFooter={hideGridFooter}
								localeText={localeText}
								columnHeaderFilterIconButton
								disableAggregation
								disableRowSelectionOnClick
								disableColumnFilter
								columnHeaderHeight={85}
								autosizeOptions={{
									columns: gridColumns,
									includeHeaders: true,
									includeOutliers: true,
								}}
								rowSelectionModel={state.rowSelectionModel}
								onRowSelectionModelChange={onRowSelectionModel}
								filterDebounceMs={500}
								rowGroupingModel={state.groupingModel}
								onRowGroupingModelChange={handleRowGroupingModelChange}
								disablePivoting
							/>
						</CardContent>
					</Card>

					<ConfirmDialog
						title={strings.Delete}
						body={strings.DeleteAlertText}
						open={!!state.selectedId && state.confirmDeleteOpen}
						cancelButtonText={strings.Cancel}
						okButtonText={strings.Delete}
						onCancel={handleCloseConfirmDialog}
						onConfirm={() => onDelete(state.selectedId)}
					/>
				</GridDataContext.Provider>

				<Stack>
					<RelationGrid
						modelConfig={modelConfig}
						openRelationIndex={state.openRelationIndex}
						data={state}
						onClearSelection={clearSelection}
						onGridStartFetchData={handleOnGridStartFetchData}
						ref={relationGridRef}
					/>
				</Stack>
				<AdminLogsTable
					data={state.uninserterdEntries}
					openModal={state.open}
					hideModal={hideModal}
					columns={logColumns}
					gridName={modelConfig.gridTitle || modelConfig.name}	
					title={strings.DuplcateMissingRecords}
					showHuluMsg={state.showHuluMsg}
				/>
			</Stack>
		);
	}
);

export default GridBase;
